import type {
  APIBookingDraft,
  APIBookingDraftRoom,
  APIBookingDraftSkiAncillaryServiceOption,
} from '@/scopes/checkout/infrastructure/api/types';
import type { ServiceType, ServiceMapper } from '../../../domain/types';
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper';
import { isNil, values } from 'ramda';
import type { APIBookingDraftSkiAncillaryService } from '@/scopes/checkout/infrastructure/api/types/services/ski';
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper';
import type {
  CheckoutSkiAncillaryDuration,
  CheckoutSkiAncillaryService,
  CheckoutSkiAncillaryServiceOption,
  CheckoutSkiAncillarySelectedValue,
} from '@/scopes/checkout/domain/types/services/ski';
import { getLocaleDateString } from '@sunwebgroup/ui-components';
import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
  serviceOptionMapper?: ServiceOptionMapper;
  serviceParticipantsMapper?: ServiceParticipantsMapper;
  serviceType: ServiceType;
}

const defaultSportType = 'Ski';
const defaultDuration = '6';

export const createSkiAncillaryServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutSkiAncillaryService> = (dependencies: Dependencies) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceType,
  } = dependencies;

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(
      ({ aName }) => aName === serviceName
    ) as unknown as APIBookingDraftSkiAncillaryService;

    if (!service) return null;

    const { aName, options } = service;
    const filteredServiceTypeOptions = options.filter((option) => option.serviceType === serviceTypeMapper);
    const participants =
      bookingDraft?.participants.reduce((acc, participant) => {
        const anyOptionAppliesToParticipant = filteredServiceTypeOptions.some(({ appliesToParticipants }) =>
          appliesToParticipants.includes(participant.id)
        );
        if (!anyOptionAppliesToParticipant) {
          return acc;
        }
        const selectedOption =
          filteredServiceTypeOptions
            .filter(({ isSelected }) => isSelected)
            .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id)) || undefined;

        return {
          ...acc,
          [participant.id.toString()]: {
            ...serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, filteredServiceTypeOptions),
            selectedValue: buildUniqOptionId(selectedOption),
            availableOptions: filteredServiceTypeOptions
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map((option) => buildUniqOptionId(option)),
          },
        };
      }, {}) ?? {};
    const anyParticipantHasSelectedValue = values(participants).some(({ selectedValue }) => selectedValue);

    if (filteredServiceTypeOptions.length === 0) {
      return null;
    }

    return {
      id: aName,
      type: serviceType,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      isNoneOption: buildUniqOptionId(filteredServiceTypeOptions.find(({ isNoneOption }) => isNoneOption)) ?? '',
      options: filteredServiceTypeOptions.reduce(
        (acc2: Record<string, CheckoutSkiAncillaryServiceOption>, option) => ({
          ...acc2,
          [buildUniqOptionId(option) ?? '']: {
            ...serviceOptionMapper.map(option, service, bookingDraft),
            id: buildUniqOptionId(option) ?? '',
            duration: option.duration.toString(),
            startDate: getLocaleDateString(new Date(option.startDate)),
            endDate: getLocaleDateString(new Date(option.endDate)),
            level: option.materialCategory || option.sportLevel,
            sportType: option.sportType,
          },
        }),
        {}
      ),
      editingSelectedValues: filteredServiceTypeOptions.reduce(
        (acc3: Record<string, CheckoutSkiAncillarySelectedValue>, option) => {
          if (option.isSelected) {
            option.assignedParticipants.forEach((participantId) => {
              acc3[participantId] = {
                duration: option.duration.toString(),
                value: option.code,
                id: buildUniqOptionId(option) ?? '',
                level: option.materialCategory || option.sportLevel,
                sportType: option.sportType,
                startDate: getLocaleDateString(new Date(option.startDate)),
              };
            });
          } else {
            bookingDraft.participants.forEach((participant) => {
              if (acc3[participant.id.toString()]) return;

              const defaultStartDateSkiPass = getDefaultStartDateSkiPassForParticipant(options, participant.id);

              let defaultOption =
                filteredServiceTypeOptions.find(
                  ({ duration, sportType, appliesToParticipants, startDate }) =>
                    duration.toString() === defaultDuration &&
                    sportType?.toLowerCase() === defaultSportType?.toLowerCase() &&
                    startDate === defaultStartDateSkiPass &&
                    appliesToParticipants.includes(participant.id)
                ) ||
                filteredServiceTypeOptions.find(
                  ({ duration, sportType, appliesToParticipants }) =>
                    duration.toString() === defaultDuration &&
                    sportType?.toLowerCase() === defaultSportType?.toLowerCase() &&
                    appliesToParticipants.includes(participant.id)
                );

              if (!defaultOption) {
                defaultOption = filteredServiceTypeOptions.find(({ isNoneOption }) => !isNoneOption);
              }

              acc3[participant.id.toString()] = {
                sportType: defaultOption?.sportType ?? defaultSportType,
                duration: defaultOption?.duration.toString() ?? defaultDuration,
                value: defaultOption?.code ?? '',
                id: buildUniqOptionId(defaultOption) ?? '',
                level: (defaultOption?.materialCategory || defaultOption?.sportLevel) ?? '',
                startDate: defaultOption?.startDate
                  ? getLocaleDateString(new Date(defaultOption?.startDate ?? ''))
                  : '',
              };
            });
          }
          return acc3;
        },
        {}
      ),
      durations: filteredServiceTypeOptions.reduce((acc4: Record<string, CheckoutSkiAncillaryDuration>, option) => {
        if (option.duration === 0) return acc4;
        acc4[option.duration.toString()] = {
          availableOptions: filteredServiceTypeOptions
            .filter(({ duration, isNoneOption }) => duration === option.duration && !isNoneOption)
            .map((optionToMap) => buildUniqOptionId(optionToMap) ?? ''),
          value: option.duration.toString(),
        };
        return acc4;
      }, {}),
      levels: filteredServiceTypeOptions.reduce(
        (acc5: Record<string, { levels: { type: string; appliesToParticipants: string[] }[] }>, option) => ({
          ...acc5,
          [option.sportType]: {
            levels: Array.from(
              new Map(
                filteredServiceTypeOptions
                  .filter(({ sportType, isNoneOption }) => sportType === option.sportType && !isNoneOption)
                  .map(({ materialCategory, sportLevel, appliesToParticipants }) => ({
                    type: materialCategory || sportLevel,
                    appliesToParticipants: appliesToParticipants.map((participantId) => participantId.toString()),
                  }))
                  .map((item) => [item.type, item]) // Create key-value pairs with `type` as the key
              ).values()
            ),
          },
        }),
        {}
      ),
    };
  }

  function getDefaultStartDateSkiPassForParticipant(
    options: APIBookingDraftSkiAncillaryServiceOption[],
    participantId: number
  ) {
    if (serviceTypeMapper === 'pass') {
      return '';
    }

    return (
      options.find(
        (option) =>
          option.serviceType === 'pass' && option.isSelected && option.assignedParticipants.includes(participantId)
      )?.startDate ?? ''
    );
  }

  function buildUniqOptionId(option: APIBookingDraftSkiAncillaryServiceOption | undefined) {
    if (!option) return undefined;
    return `${option.code}??${option.duration}??${option.startDate}??${option.endDate}`;
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map((participantId) => participantId.toString()),
    };
  }

  function mapSelection(service: CheckoutSkiAncillaryService) {
    const { participants, options } = service;
    return Object.values(participants ?? {}).map(({ id, selectedValue }) => {
      const selectedOption = options[selectedValue ?? ''];
      return {
        participantId: id,
        value: selectedOption?.value ?? null,
        startDate: selectedOption?.startDate,
        endDate: selectedOption?.endDate,
      };
    });
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutSkiAncillaryService,
    previousService: CheckoutSkiAncillaryService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[] = [];
    const ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[] = [];

    if (!service) {
      return { ancillaryAddMapped, ancillaryRemoveMapped };
    }

    const participantsModified = Object.keys(service?.participants).reduce(
      (modified: { id: string; previousValue: string | null; newValue: string | null }[], id) => {
        const prevParticipant = previousService?.participants[id];
        const newParticipant = service?.participants[id];

        const prevSelectedValue = prevParticipant?.selectedValue;
        const previousSelectedOption = prevSelectedValue ? service.options[prevSelectedValue] : null;

        const {
          value: previousValue,
          duration: previousDuration,
          startDate: previousStartDate,
        } = previousSelectedOption || {};

        const prevSelectedOptionWithNewId =
          Object.values(service?.options).find(
            ({ value, duration, startDate }) =>
              value === previousValue && duration === previousDuration && startDate === previousStartDate
          )?.id ?? null;

        const newSelectedValue = newParticipant?.selectedValue;

        if (prevSelectedOptionWithNewId !== newSelectedValue) {
          modified.push({
            id,
            previousValue: prevSelectedOptionWithNewId,
            newValue: newSelectedValue,
          });
        }

        return modified;
      },
      []
    );

    participantsModified.forEach((participant) => {
      if (participant.newValue) {
        const participantAgeCategory = service.participants[participant.id]?.ageCategory;
        const option = service.options[participant.newValue];
        const participantPrice = option.priceBreakdown
          ? option.priceBreakdown.find((priceAgeCategory) => priceAgeCategory.ageCategory === participantAgeCategory)
              ?.price
          : option?.price;

        ancillaryAddMapped.push({
          id: option.value,
          name: option?.label || '',
          price: participantPrice || 0,
          category: `Ancillary > Additional cost > ${service.type}`,
          quantity: 1,
          serviceType: service.type,
          extraProperties: {},
        });
      }
      if (participant.previousValue) {
        const option = service.options[participant.previousValue];
        const participantAgeCategory = service.participants[participant.id]?.ageCategory;
        const participantPrice = option.priceBreakdown
          ? option.priceBreakdown.find((priceAgeCategory) => priceAgeCategory.ageCategory === participantAgeCategory)
              ?.price
          : option?.price;

        ancillaryRemoveMapped.push({
          id: option.value,
          name: option?.label || '',
          price: participantPrice || 0,
          category: `Ancillary > Additional cost > ${service.type}`,
          quantity: 1,
          serviceType: service.type,
          extraProperties: {},
        });
      }
    });

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
